import { createFileRoute } from '@tanstack/react-router';
import env from '../../environment';
import {
  ArrowRightIcon,
  EyeIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { basicHead } from '../../utility/head';
import { getButtonStyles } from '@sc/shared-ui';
import { PricingSlider } from '../../components/PricingSlider';
import { UsageCreditGuide } from '../../components/UsageCreditGuide';
import Breadcrumbs from '../../components/Breadcrumbs';
import { LogoSlider } from '../../components/LogoSlider';
import { PricingPlans } from '../../components/PricingPlans';
import { FaqList } from '../../components/FaqList';
import SpeedometerIcon from '../../assets/icons/speedometer.svg?react';
import {
  CLASSIFY_EFFORT_CREDIT_BREAKDOWN,
  creditsForSpendGbp,
  formatPriceGbp,
  formatPricePerCredit,
  GBP_PER_CREDIT,
  ORG_CONCURRENT_CATEGORY_DRAINS,
  PER_PROJECT_CONCURRENT_CATEGORY_DRAINS,
  USAGE_CREDIT_RANGE_NOTE,
  USAGE_CREDIT_RANGES,
  USAGE_CREDIT_STEPS,
} from '../../assets/data/pricing';

const usageCreditBand = (action: string): string =>
  USAGE_CREDIT_RANGES.find((row) => row.action === action)?.credits ?? '';

const firstCreditStep = USAGE_CREDIT_STEPS[0];
const firstCreditPriceLabel = formatPriceGbp(
  firstCreditStep?.priceGbp ?? 75
);

const examplePackGbp = 75;

const FAQS = [
  {
    q: 'How does credit pricing work?',
    a: `Credits cost a fixed ${formatPricePerCredit(GBP_PER_CREDIT)} each. Extraction effort: ${CLASSIFY_EFFORT_CREDIT_BREAKDOWN} credits per call. ${USAGE_CREDIT_RANGE_NOTE} Buy packs or a monthly credit subscription from ${firstCreditPriceLabel}. Example: ${formatPriceGbp(examplePackGbp)} buys ${creditsForSpendGbp(examplePackGbp).toLocaleString('en-GB')} credits.`,
  },
  {
    q: 'What does each action cost?',
    a: `Extraction Low is ${usageCreditBand('Extraction Low').toLowerCase()} credit, Medium is ${usageCreditBand('Extraction Medium')} and High is ${usageCreditBand('Extraction High').toLowerCase()}. ${USAGE_CREDIT_RANGE_NOTE} Crawls cost 1 credit per Firecrawl credit on the platform key. A project Firecrawl key is billed on your Firecrawl account and is not a Starkcrawl credit. Reclassify costs ~1 credit per call on the platform key (approximate). A Requesty key on the project skips that credit and is billed on your Requesty account using Jev model.`,
  },
  {
    q: 'Are there throughput limits?',
    a: `Yes. Organisations can run up to ${ORG_CONCURRENT_CATEGORY_DRAINS} concurrent category drain steps, and each project is capped at ${PER_PROJECT_CONCURRENT_CATEGORY_DRAINS}.`,
  },
  {
    q: 'Can I try Stark Crawl for free?',
    a: 'Yes! Sign up and start a free trial with no credit card required. Configure projects, run discovery, and test extraction — then add credits when you are ready to scale.',
  },
  {
    q: 'What happens if I run out of credits?',
    a: 'Crawls and discovery pause when credits are exhausted. Buy another pack or start a monthly credit subscription anytime. Topic discovery and extraction always need a credit balance.',
  },
  {
    q: 'Can I bring my own Firecrawl API key?',
    a: `Yes. Crawls on the platform key cost 1 credit per Firecrawl credit. Add a Firecrawl API key on a project to bill that project's crawls on your Firecrawl account instead of Starkcrawl credits. Topic discovery and extraction still spend the credit balance.`,
  },
  {
    q: 'Can I change credit pack size later?',
    a: 'Yes. Buy packs or adjust monthly credit subscriptions from Billing in the app anytime.',
  },
  {
    q: 'Is there a discount for charities or startups?',
    a: 'Yes, we offer discounted rates for registered charities and early-stage startups. Get in touch via our contact page to discuss your situation.',
  },
];

export const Route = createFileRoute('/_marketing/pricing')({
  component: RouteComponent,
  ssr: true,
  loader: ({ location }) => ({
    pathname: location.pathname,
    seo: {
      title: 'Web crawling pricing - Stark Crawl',
      description:
        `Simple usage credits at 0.1p each. Extraction effort: ${CLASSIFY_EFFORT_CREDIT_BREAKDOWN} credits per call. Buy packs, or bring your own Firecrawl key.`,
      keywords:
        'web crawling pricing, market research crawl pricing, structured data extraction cost, Stark Crawl pricing',
      schema: {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(({ q, a }) => ({
          '@type': 'Question',
          name: q,
          acceptedAnswer: { '@type': 'Answer', text: a },
        })),
      },
    },
  }),
  head: basicHead,
});

function RouteComponent() {
  return (
    <div className="relative overflow-x-hidden">
      <section className="pt-15 lg:pt-20 pb-15 px-5">
        <div className="max-w-315 mx-auto">
          <Breadcrumbs
            center
            items={[{ label: 'Home', path: '/' }, { label: 'Pricing' }]}
          />
          <h1 className="font-grotesque text-[2.625rem] text-center leading-[1.15] lg:text-[4.375rem] lg:leading-none font-medium text-white mt-5">
            Simple billing that grows with you
          </h1>
          <p className="text-base leading-6 text-grey-200 text-center max-w-140 mx-auto mt-5">
            Prepaid usage credits at a fixed rate. Extraction effort:{' '}
            {CLASSIFY_EFFORT_CREDIT_BREAKDOWN} credits per call. Buy packs as
            you need them, or start a monthly credit subscription.
          </p>
        </div>
      </section>

      <section className="pb-25 px-5">
        <PricingPlans />
      </section>

      <section id="pricing-calculator" className="pb-25 px-5 scroll-mt-20">
        <div className="max-w-315 mx-auto grid gap-10 lg:grid-cols-2 lg:gap-20 items-start">
          <div>
            <h2 className="font-grotesque text-4xl leading-[1.15] lg:text-[3.5rem] lg:leading-[1.1] font-medium text-white">
              Estimate usage credits
            </h2>
            <p className="text-base leading-6 text-grey-200 mt-5">
              Slide the credit spend to see how many credits you get at{' '}
              {formatPricePerCredit(GBP_PER_CREDIT)}/credit.
            </p>
            <div className="mt-8">
              <UsageCreditGuide />
            </div>
            <p className="text-sm text-text-low-priority mt-8">
              * Credit packs exclude VAT. Ranges are typical.
            </p>
          </div>
          <div className="bg-background-tertiary border border-grey-t-800 rounded-xs p-8">
            <PricingSlider />
            <div className="mt-8">
              <a
                href={env.appUrl + '/register'}
                target="_blank"
                rel="noreferrer noopener"
                {...getButtonStyles({
                  type: 'primary',
                  size: 'large',
                  additionalClassnames: 'gap-x-2 w-full!',
                })}
              >
                Try for free
                <ArrowRightIcon className="w-4.5 h-4.5" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-25 px-5">
        <div className="max-w-315 mx-auto text-center">
          <p className="font-grotesque text-xl font-bold text-primary-400 uppercase leading-5 mb-5">
            Trusted by
          </p>
          <LogoSlider>
            <img src="/logos/able.svg" alt="Able" className="max-h-12.5" />
            <img src="/logos/aduro.svg" alt="Aduro" className="max-h-12.5" />
            <img
              src="/logos/bsquared.webp"
              alt="BSQuared"
              className="max-h-12.5 opacity-30"
            />
            <img src="/logos/tfp.svg" alt="TFP" className="max-h-12.5" />
            <img
              src="/logos/unibeez.svg"
              alt="Unibeez"
              className="max-h-12.5"
            />
          </LogoSlider>
        </div>
      </section>

      <section className="pb-25 mt-10 px-5">
        <div className="max-w-315 mx-auto text-center">
          <p className="font-grotesque text-xl font-bold text-primary-400 uppercase leading-5 mb-3">
            Billing
          </p>
          <h2 className="font-grotesque text-4xl leading-[1.2] lg:text-[4rem] lg:leading-16 font-medium text-white mb-15">
            Transparent charges
          </h2>
          <div className="flex flex-col lg:flex-row gap-10 items-start">
            {[
              {
                Icon: SpeedometerIcon,
                title: 'Fixed credit rate',
                body: `Every credit costs ${formatPricePerCredit(GBP_PER_CREDIT)}. Extraction effort: ${CLASSIFY_EFFORT_CREDIT_BREAKDOWN} credits per call. Up to ${ORG_CONCURRENT_CATEGORY_DRAINS} concurrent category drains per org (${PER_PROJECT_CONCURRENT_CATEGORY_DRAINS} per project).`,
              },
              {
                Icon: CalendarIcon,
                title: 'Packs or monthly',
                body: 'Buy credit packs as you need them, or start a monthly credit subscription for predictable top-ups. No platform tier fee.',
              },
              {
                Icon: EyeIcon,
                title: 'Optional BYO Firecrawl',
                body: `Crawls on the platform key cost 1 credit per Firecrawl credit. Add your Firecrawl API key on a project to bill that project's crawls on your account. Topic discovery and extraction still spend the credit balance.`,
              },
            ].map(({ Icon, title, body }) => (
              <div
                key={title}
                className="flex flex-col gap-4 items-center flex-1"
              >
                <div className="bg-primary-t-1000 inline-flex items-center justify-center p-5 rounded shrink-0">
                  <Icon
                    className="size-6.5 text-primary-400"
                    aria-hidden="true"
                  />
                </div>
                <h3 className="font-grotesque text-2xl leading-7.5 font-semibold text-white">
                  {title}
                </h3>
                <p className="text-base leading-6 text-grey-200">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-25 pt-10 px-5">
        <div className="max-w-315 mx-auto">
          <h2 className="font-grotesque text-4xl leading-[1.2] lg:text-[4rem] lg:leading-16 font-medium text-white mb-15 text-center">
            Pricing FAQs
          </h2>
          <FaqList faqs={FAQS} />
        </div>
      </section>
    </div>
  );
}
